import { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../axiosInstance';

const PAYMENTS_BASE = '/finance/payments';

const PAYMENT_METHOD_MAP = {
  'Stripe': 'stripe',
  'Bank Transfer': 'bank_transfer',
  'Visa': 'visa',
  'Mastercard': 'mastercard',
  'Cash': 'cash',
  'SEPA': 'sepa',
  'SWIFT': 'swift',
  'Amex': 'amex',
  'PayPal': 'paypal',
  'Discover': 'discover',
  'Direct Debit': 'direct_debit',
  'Payment Terminal': 'payment_terminal',
};

const PAYMENT_METHOD_DISPLAY = {
  stripe: 'Stripe',
  bank_transfer: 'Bank Transfer',
  visa: 'Visa',
  mastercard: 'Mastercard',
  cash: 'Cash',
  sepa: 'SEPA',
  swift: 'SWIFT',
  amex: 'Amex',
  paypal: 'PayPal',
  discover: 'Discover',
  direct_debit: 'Direct Debit',
  payment_terminal: 'Payment Terminal',
};

const ACCOUNT_MAP = {
  'Main Business Account': 'main_business_account',
  'Cash Register - Office': 'cash_register_office',
  'Credit Card Processing': 'credit_card_processing',
};

const LINKED_TO_MAP = {
  'Booking Payment': 'booking_payment',
  'Deposit': 'deposit',
  'Balance Payment': 'balance_payment',
  'Penalty': 'penalty',
};

const LINKED_TO_DISPLAY = {
  booking_payment: 'Booking Payment',
  deposit: 'Deposit',
  balance_payment: 'Balance Payment',
  penalty: 'Penalty',
};

const STATUS_DISPLAY = {
  pending: 'Pending',
  completed: 'Completed',
  failed: 'Failed',
  cancelled: 'Cancelled',
  refunded: 'Refunded',
};

const STATUS_API_MAP = {
  'Pending': 'pending',
  'Completed': 'completed',
  'Failed': 'failed',
  'Cancelled': 'cancelled',
  'Refunded': 'refunded',
};

const PAYMENT_TYPE_DISPLAY = {
  incoming_payment: 'Incoming Payment',
  outgoing_payment: 'Outgoing Payment',
  refund: 'Refund',
};

const PAYMENT_TYPE_API_MAP = {
  'Incoming Payment': 'incoming_payment',
  'Outgoing Payment': 'outgoing_payment',
  'Refund': 'refund',
};

const CLIENT_TYPE_DISPLAY = {
  client_payment: 'Client Payment',
  supplier_payment: 'Supplier Payment',
  client_refund: 'Client Refund',
  supplier_refund: 'Supplier Refund',
};

const DIRECTION_DISPLAY = {
  incoming: 'Incoming',
  outgoing: 'Outgoing',
};

export const toApiMethod = (displayMethod) =>
  PAYMENT_METHOD_MAP[displayMethod] || displayMethod?.toLowerCase()?.replace(/\s+/g, '_') || 'bank_transfer';

export const toDisplayMethod = (apiMethod) =>
  PAYMENT_METHOD_DISPLAY[apiMethod] || apiMethod || '—';

export const toApiLinkedTo = (displayLinkedTo) =>
  LINKED_TO_MAP[displayLinkedTo] || displayLinkedTo?.toLowerCase()?.replace(/\s+/g, '_') || null;

export const toDisplayLinkedTo = (apiLinkedTo) =>
  LINKED_TO_DISPLAY[apiLinkedTo] || apiLinkedTo || 'Booking Payment';

export const toDisplayStatus = (apiStatus) =>
  STATUS_DISPLAY[apiStatus] || apiStatus || 'Pending';

export const toApiStatus = (displayStatus) =>
  STATUS_API_MAP[displayStatus] || displayStatus?.toLowerCase() || 'pending';

export const toDisplayPaymentType = (apiType) =>
  PAYMENT_TYPE_DISPLAY[apiType] || apiType || '—';

export const toApiPaymentType = (displayType) =>
  PAYMENT_TYPE_API_MAP[displayType] || displayType?.toLowerCase()?.replace(/\s+/g, '_') || 'incoming_payment';

export const toDisplayClientType = (apiClient) =>
  CLIENT_TYPE_DISPLAY[apiClient] || apiClient || '—';

export const toDisplayDirection = (apiDirection) =>
  DIRECTION_DISPLAY[apiDirection] || apiDirection || '—';

export const toApiAccount = (displayAccount) =>
  ACCOUNT_MAP[displayAccount] || 'main_business_account';

export const mapApiPaymentToUi = (p) => ({
  id: p.id,
  date: p.payment_date,
  method: toDisplayMethod(p.payment_method),
  reference: p.transaction_id || p.payment_reference || '—',
  amount: parseFloat(p.amount || 0),
  // Processing fee on an incoming client payment — mapped so the edit modal can
  // pre-fill and update it (0 → null so the field shows empty rather than "0").
  transactionFee: parseFloat(p.transaction_fee || 0) || null,
  currency: p.currency || 'EUR',
  linkedTo: toDisplayLinkedTo(p.linked_to),
  status: toDisplayStatus(p.status),
  notes: p.notes || '',
  createdBy: p.created_by ? `User #${p.created_by}` : 'System',
  supplier: p.supplier_name || '',
  supplierEmail: p.supplier_email || '',
  component: p.component || '',
  // A supplier payment covers a single booking component. The UI (supplier
  // "View All" table + detail panel) expects a `products` array, so derive one
  // from the component. Without this the modal crashes on `products.map()`.
  products: p.component ? [{ type: p.component, name: p.component }] : [],
  // The API has no per-record partial amount: a completed payment is fully
  // paid, anything else is not yet paid. Drives the Paid/Balance columns and
  // the detail progress bar (which otherwise render NaN).
  paidAmount: (p.status === 'completed' || p.status === 'paid')
    ? parseFloat(p.amount || 0)
    : 0,
  customerName: p.customer_name || '',
  customerEmail: p.customer_email || '',
  airlinePnr: p.airline_pnr || '',
  supplierReferenceNo: p.supplier_reference_no || '',
  transactionId: p.transaction_id || '',
  // SUNSKY payment logic fields: provider (Worldline/Stripe/…) as distinct from
  // the method, provider settlement reference for bank reconciliation, and — on
  // supplier payments — who bears the transaction fee.
  provider: p.payment_provider || '',
  settlementReference: p.settlement_reference || '',
  feePaidBy: p.fee_paid_by || null,
  // Supplier payments: internal division over rooms/travelers ({mode, productId,
  // rooms[], travelers[]}). MySQL JSON arrives as an object, but parse defensively.
  allocation: (() => {
    if (!p.allocation) return null;
    if (typeof p.allocation === 'string') {
      try { return JSON.parse(p.allocation); } catch { return null; }
    }
    return p.allocation;
  })(),
  // Client incoming: is the client covering the transaction cost? (default true)
  clientCoversFee: p.client_covers_fee !== false,
  paymentType: p.payment_type,
  paymentTypeDisplay: PAYMENT_TYPE_DISPLAY[p.payment_type] || p.payment_type || '—',
  direction: p.direction,
  directionDisplay: DIRECTION_DISPLAY[p.direction] || p.direction || '—',
  clientType: p.client,
  clientTypeDisplay: CLIENT_TYPE_DISPLAY[p.client] || p.client || '—',
  account: p.account,
  accountId: p.account_id || null,
  _raw: p,
});

export const useGetBookingPayments = (bookingReference) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchPayments = useCallback(async () => {
    if (!bookingReference) return;
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get(`${PAYMENTS_BASE}/booking/${bookingReference}`);
      const res = response.data;
      if (res?.success) {
        const raw = res.data;
        setData({
          client: (raw.client || []).map(mapApiPaymentToUi),
          supplier: (raw.supplier || []).map(mapApiPaymentToUi),
          refunds: (raw.refunds || []).map(mapApiPaymentToUi),
          summary: raw.summary || {
            client: { total: 0, collected: 0, pending: 0, refunds: 0, outstanding: 0 },
            supplier: { total: 0, paid: 0, openPayables: 0, refunds: 0, outstanding: 0 },
          },
        });
      } else {
        setError(res?.message || 'Failed to load payments');
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to load payments';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [bookingReference]);

  useEffect(() => { fetchPayments(); }, [fetchPayments]);

  return { data, loading, error, refetch: fetchPayments };
};

export const useCreatePayment = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const clearError = useCallback(() => setError(null), []);

  const execute = useCallback(async (payload) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.post(PAYMENTS_BASE, payload);
      const res = response.data;
      if (res?.success) return { success: true, data: res.data };
      return { success: false, message: res?.message || 'Failed to create payment', errors: res?.errors };
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to create payment';
      const errors = err.response?.data?.errors || [];
      setError(msg);
      return { success: false, message: msg, errors };
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error, clearError };
};

export const useUpdatePayment = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const clearError = useCallback(() => setError(null), []);

  const execute = useCallback(async (paymentId, payload) => {
    if (!paymentId) return { success: false, message: 'Payment ID is required' };
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.put(`${PAYMENTS_BASE}/${paymentId}`, payload);
      const res = response.data;
      if (res?.success) return { success: true, data: res.data };
      return { success: false, message: res?.message || 'Failed to update payment', errors: res?.errors };
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to update payment';
      setError(msg);
      return { success: false, message: msg };
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error, clearError };
};

export const useDeletePayment = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const clearError = useCallback(() => setError(null), []);

  const execute = useCallback(async (paymentId) => {
    if (!paymentId) return { success: false, message: 'Payment ID is required' };
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.delete(`${PAYMENTS_BASE}/${paymentId}`);
      const res = response.data;
      if (res?.success) return { success: true, data: res.data };
      return { success: false, message: res?.message || 'Failed to delete payment' };
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to delete payment';
      setError(msg);
      return { success: false, message: msg };
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error, clearError };
};
